import { logger } from '../services/logger';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from "../lib/db.ts";
import { useAuth } from "../lib/auth.tsx";
import { Check, X, Search, ArrowLeft, Filter, FileText, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { ConfirmationModal } from "../components/common/ConfirmationModal.tsx";

interface FICADocument {
  id: string;
  user_id: string;
  document_type: 'id_copy' | 'proof_of_address' | 'proof_of_bank_details';
  file_url: string;
  file_name: string | null;
  file_size: number | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_full_name?: string;
}

interface FICAVerification {
  user_id: string;
  is_verified: boolean;
  verified_at: string | null;
  verified_by: string | null;
  user_email?: string;
  user_full_name?: string;
}

const DOCUMENT_TYPE_LABELS = {
  id_copy: 'Copy of ID',
  proof_of_address: 'Proof of Address',
  proof_of_bank_details: 'Proof of Bank Details'
};

export function AdminFICA() {
  const [documents, setDocuments] = useState<FICADocument[]>([]);
  const [verifications, setVerifications] = useState<FICAVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [selectedDocument, setSelectedDocument] = useState<FICADocument | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchDocuments();
    fetchVerifications();
  }, [user, navigate, filterStatus]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      if (!user || (user.app_metadata?.role !== 'admin' && user.app_metadata?.role !== 'superuser')) {
        navigate('/markets');
        return;
      }

      let query = supabase
        .from('fica_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Fetch user info for all unique user IDs
      const userIds = [...new Set((data || []).map((doc: any) => doc.user_id))];
      const userEmailMap: Record<string, { email: string; full_name: string }> = {};

      if (userIds.length > 0) {
        // Fetch user emails using RPC function
        const { data: userInfoData, error: userInfoError } = await supabase
          .rpc('get_users_basic_info', { p_user_ids: userIds });

        if (!userInfoError && userInfoData) {
          userInfoData.forEach((userInfo: any) => {
            userEmailMap[userInfo.id] = {
              email: userInfo.email || '',
              full_name: userInfo.full_name || 'Unknown'
            };
          });
        } else {
          logger.error('Error fetching user info:', userInfoError);
        }
      }

      // Transform data to include user info
      const transformedData: FICADocument[] = (data || []).map((doc: any) => {
        const userInfo = userEmailMap[doc.user_id] || { email: '', full_name: 'Unknown' };
        return {
          ...doc,
          user_email: userInfo.email,
          user_full_name: userInfo.full_name
        };
      });

      setDocuments(transformedData);
    } catch (err) {
      logger.error('Error fetching FICA documents:', err);
      setError('Failed to load FICA documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchVerifications = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('fica_verification')
        .select('*');

      if (fetchError) throw fetchError;

      // Fetch user info for all unique user IDs
      const userIds = [...new Set((data || []).map((ver: any) => ver.user_id))];
      const userEmailMap: Record<string, { email: string; full_name: string }> = {};

      if (userIds.length > 0) {
        // Fetch user emails using RPC function
        const { data: userInfoData, error: userInfoError } = await supabase
          .rpc('get_users_basic_info', { p_user_ids: userIds });

        if (!userInfoError && userInfoData) {
          userInfoData.forEach((userInfo: any) => {
            userEmailMap[userInfo.id] = {
              email: userInfo.email || '',
              full_name: userInfo.full_name || 'Unknown'
            };
          });
        } else {
          logger.error('Error fetching user info:', userInfoError);
        }
      }

      const transformedData: FICAVerification[] = (data || []).map((ver: any) => {
        const userInfo = userEmailMap[ver.user_id] || { email: '', full_name: 'Unknown' };
        return {
          ...ver,
          user_email: userInfo.email,
          user_full_name: userInfo.full_name
        };
      });

      setVerifications(transformedData);
    } catch (err) {
      logger.error('Error fetching FICA verifications:', err);
    }
  };

  const handleReview = (document: FICADocument, action: 'approve' | 'reject') => {
    setSelectedDocument(document);
    setReviewAction(action);
    setRejectionReason('');
    setShowReviewModal(true);
  };

  const confirmReview = async () => {
    if (!selectedDocument || !reviewAction || !user) return;

    try {
      const updateData: any = {
        status: reviewAction === 'approve' ? 'approved' : 'rejected',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString()
      };

      if (reviewAction === 'reject' && rejectionReason.trim()) {
        updateData.rejection_reason = rejectionReason.trim();
      }

      const { error: updateError } = await supabase
        .from('fica_documents')
        .update(updateData)
        .eq('id', selectedDocument.id);

      if (updateError) throw updateError;

      await fetchDocuments();
      await fetchVerifications();
      setShowReviewModal(false);
      setSelectedDocument(null);
      setReviewAction(null);
      setRejectionReason('');
    } catch (err) {
      logger.error('Error reviewing document:', err);
      setError('Failed to update document status');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const filteredDocuments = documents.filter(doc => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        doc.user_email?.toLowerCase().includes(searchLower) ||
        doc.user_full_name?.toLowerCase().includes(searchLower) ||
        DOCUMENT_TYPE_LABELS[doc.document_type].toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const isUserVerified = (userId: string): boolean => {
    const verification = verifications.find(v => v.user_id === userId);
    return verification?.is_verified ?? false;
  };

  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const verifiedCount = verifications.filter(v => v.is_verified).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">FICA Document Review</h1>
              <p className="text-sm text-gray-600 mt-1">Review and approve user FICA documents</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Verified Users</p>
                <p className="text-2xl font-bold text-green-600">{verifiedCount}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Documents</p>
                <p className="text-2xl font-bold text-gray-800">{documents.length}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by user email or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    filterStatus === status
                      ? 'bg-wayja-teal text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Documents Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No documents found
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/admin/users/${doc.user_id}`}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                            >
                              {doc.user_full_name || 'Unknown'}
                            </Link>
                            {isUserVerified(doc.user_id) && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                FICA Verified
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            <Link
                              to={`/admin/users/${doc.user_id}`}
                              className="text-gray-500 hover:text-blue-600 hover:underline"
                            >
                              {doc.user_email}
                            </Link>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {DOCUMENT_TYPE_LABELS[doc.document_type]}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(doc.status)}
                          <span className={`text-sm font-medium ${
                            doc.status === 'approved' ? 'text-green-600' :
                            doc.status === 'rejected' ? 'text-red-600' :
                            'text-yellow-600'
                          }`}>
                            {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                          </span>
                        </div>
                        {doc.rejection_reason && (
                          <div className="text-xs text-red-600 mt-1">
                            {doc.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </a>
                          {doc.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleReview(doc, 'approve')}
                                className="text-green-600 hover:text-green-900 flex items-center gap-1"
                              >
                                <Check className="w-4 h-4" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleReview(doc, 'reject')}
                                className="text-red-600 hover:text-red-900 flex items-center gap-1"
                              >
                                <X className="w-4 h-4" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Review Modal */}
        {showReviewModal && selectedDocument && reviewAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-auto shadow-xl">
              <h3 className="text-xl font-bold mb-4">
                {reviewAction === 'approve' ? 'Approve Document' : 'Reject Document'}
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>User:</strong>{' '}
                  <Link
                    to={`/admin/users/${selectedDocument.user_id}`}
                    className="text-blue-600 hover:text-blue-900 hover:underline"
                  >
                    {selectedDocument.user_full_name} ({selectedDocument.user_email})
                  </Link>
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Document:</strong> {DOCUMENT_TYPE_LABELS[selectedDocument.document_type]}
                </p>
              </div>
              {reviewAction === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason (required)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Please provide a reason for rejection..."
                    rows={4}
                  />
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedDocument(null);
                    setReviewAction(null);
                    setRejectionReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReview}
                  disabled={reviewAction === 'reject' && !rejectionReason.trim()}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    reviewAction === 'approve'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed'
                  }`}
                >
                  {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

